// ProjectTableRow.jsx
import {
    Avatar,
    AvatarGroup,
    Box,
    Chip, CircularProgress,
    IconButton,
    Stack,
    TableCell,
    TableRow,
    Tooltip,
    Typography
} from "@mui/material";
import {grey, yellow} from "@mui/material/colors";
import {Link} from "react-router-dom";
import {IconStar, IconStarFilled} from "@tabler/icons-react";
import React, {useContext, useEffect, useMemo, useState} from "react";
import {SignInContext, TOKEN} from "../../../context/SignInContext";
import AreaChart from "../../project/dashboard/components/AreaChart";
import {ProjectStageProgress, ProjectStatus} from "./project-stage-status";
import {ProjectRowMenu} from "./project-row-menu";
import {Context as AppContext, ProjectRoles} from "../../../context/AppContext";
import {toast} from "react-toastify";

const ProjectRichTableRow = ({project, categories, languages, organizations}) => {
    const {updateProject, getUserProjects} = useContext(AppContext);
    const [chartData, setChartData] = useState(null); // For future chart fetch
    const {currentUser} = useContext(SignInContext);
    const [loading, setLoading] = useState(false);

    const category = categories.find(i => i.id === project?.settings?.category);
    const lang = languages.find(i => i.id === project?.settings?.language);
    const org = organizations.find(o => o.id === project?.settings?.organization);

    // Optional: fetch chart data if needed
    useEffect(() => {
        // Example:
        // fetchChartData(project.id).then(data => setChartData(data));
        const fetchChartData = async () => {
            const api = project?.configuration?.conversation_logger_api || "";
            const path = `/api/conversation/statistics/response-time?flow=${project?.id}`;
            try {
                const response = await fetch(
                    `${api?.endsWith("/") ? api?.slice(0, api?.length - 1) : api}${path}`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem(TOKEN)}`
                        }
                    }
                );
                const jsonData = await response?.json();
                const responseTime = jsonData?.data || jsonData || [];
                const data = responseTime?.map(i => ({...i, dateCreated: i?.hour, duration: i?.countActiveCalls}))
                setChartData(data);
            } catch (e) {
                console.error(e);
            }
        }
        if (project?.configuration?.is_live && project?.configuration?.conversation_logger_api) {
            fetchChartData().then(() => console.log("Fetch chart data"))
        }
    }, [project?.id]);

    const onUpdateFavourites = async () => {
        if (loading) {
            return;
        }
        let favourites = project?.settings?.favourites || [];
        if (isSelected) {
            favourites = favourites?.filter(i => i !== currentUser?.username)
        } else {
            favourites.push(currentUser?.username);
        }
        let formData = {...project}
        formData.settings.favourites = favourites;
        setLoading(true);
        try {
            console.log(formData)
            await updateProject(formData);
            await getUserProjects();
            toast.success("Updated successfully");
            // await FlowProjectRepository.updateFlow({id: project?.id})
        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false);
        }
    }

    const favourites = project?.settings?.favourites || [];

    const isSelected = useMemo(() => {
        return favourites?.includes(currentUser?.username);
    }, [favourites, currentUser]);

    const team = project?.team || org?.members?.map(i => i?.member);

    const role = useMemo(() => {
        const isAdmin = currentUser?.roles?.includes("admin");
        if (isAdmin) {
            return ProjectRoles.ADMIN
        }
        const member = project?.members?.find(i => i?.username === currentUser?.username);
        return member?.role || null;
    }, [currentUser, project?.members])
    const hasActivity = chartData?.some(d => Number(d.duration) > 0);

    return (
        <TableRow
            key={project?.id}
            sx={{p: 2, mb: 2, borderRadius: 4, border: 0, height: 150, "& > td": {bgcolor: "#fff"}}}
            className="border-shadow"
        >
            <TableCell padding="checkbox" sx={{
                borderBottom: 'none',
                px: 2,
                maxWidth: 24,
                borderBottomLeftRadius: "inherit",
                borderTopLeftRadius: "inherit"
            }}>
                <IconButton
                    disabled={loading}
                    onClick={onUpdateFavourites}
                    sx={{color: isSelected ? yellow[700] : "default"}}
                >
                    {loading ? <CircularProgress size={18}/> : isSelected ? <IconStarFilled/> : <IconStar/>}
                </IconButton>
            </TableCell>

            <TableCell padding={"checkbox"} sx={{borderBottom: 'none'}}>
                <Stack direction="column" gap={2}>
                    <Box>
                        <Typography
                            component={Link}
                            to={`/project/${project?.id}`}
                            sx={{
                                textDecoration: "none",
                                color: grey[900],
                                "&:hover": {bgcolor: grey[100], textDecoration: "underline"}
                            }}
                            variant="h4"
                            fontSize="1.2em"
                            fontWeight={600}
                            noWrap
                        >
                            {project?.name}
                        </Typography>
                    </Box>
                    <Box>
                        {category && (
                            <Chip
                                sx={{bgcolor: grey[200], mr: 1, fontSize: "0.9em"}}
                                size="small"
                                label={category?.name}
                            />
                        )}
                        {lang && (
                            <Chip
                                sx={{bgcolor: grey[200], mr: 1, fontSize: "0.9em"}}
                                size="small"
                                label={lang?.language}
                            />
                        )}
                    </Box>
                </Stack>
            </TableCell>

            <TableCell padding="checkbox" sx={{borderBottom: 'none', px: 2, maxWidth: "100px"}}>
                <AvatarGroup>
                    {team?.slice(0, 3)?.map(member => (
                        <Tooltip title={member} key={member}>
                            <Avatar
                                size="small"
                                sx={{
                                    bgcolor: grey[200],
                                    fontSize: 14,
                                    color: "secondary.main",
                                    width: 35,
                                    height: 35
                                }}
                                alt={member}
                            >
                                {member?.slice(0, 2)?.toUpperCase()}
                            </Avatar>
                        </Tooltip>
                    ))}
                </AvatarGroup>
                {team?.length - 3 > 0 && (
                    <Tooltip
                        title={
                            <Stack>
                                {team?.slice(3)?.map(member => (
                                    <Typography fontSize="0.95em" sx={{p: 0.2}} key={member}>
                                        {member}
                                    </Typography>
                                ))}
                            </Stack>
                        }
                    >
                        <Typography sx={{color: grey[600], fontSize: "0.9em", pt: 1}} align={"end"} noWrap>
                            {team?.length - 3} more people
                        </Typography>
                    </Tooltip>
                )}
            </TableCell>


            <TableCell padding={"checkbox"} sx={{
                borderBottom: 'none', px: 2, pt: 2,
                maxWidth: "150px", minWidth: "100px",
                height: "90px", overflow: "hidden"
            }}>
                {project?.configuration?.is_live && chartData ? (
                    hasActivity ? (
                        <Box sx={{display: "flex", justifyContent: "center"}}>
                            <AreaChart data={chartData} colors={["#3f51b5"]} height={70} width={130}/>
                        </Box>
                    ) : (
                        <Box sx={{
                            height: "70px", width: "130px", mx: "auto",
                            display: "flex", alignItems: "center", justifyContent: "center"
                        }}>
                            <Box sx={{
                                width: "100%", height: "2px",
                                backgroundImage: `repeating-linear-gradient(90deg, ${grey[400]} 0, ${grey[400]} 6px, transparent 6px, transparent 12px)`
                            }}/>
                        </Box>
                    )
                ) : (
                    <Typography align={"center"}>Project is not active</Typography>
                )}
            </TableCell>
            <TableCell padding={"checkbox"} sx={{borderBottom: 'none', px: 2}}>
                <ProjectStageProgress status={project?.settings?.status || ProjectStatus.BUILDING_SCENARIOS}/>
            </TableCell>
            {
                role !== ProjectRoles.AGENT ?
                    <TableCell padding={"checkbox"} sx={{
                        borderBottom: 'none',
                        px: 0,
                        textAlign: "center",
                        borderBottomRightRadius: "inherit",
                        borderTopRightRadius: "inherit"
                    }}>
                        <Box>
                            <ProjectRowMenu project={project}/>
                        </Box>
                    </TableCell> : null
            }

        </TableRow>
    );
};

export default ProjectRichTableRow;
